import { useForm } from 'react-hook-form';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { __, any, append, applySpec, assoc, chain, compose, cond, equals, evolve, filter, find, findIndex, flatten, fromPairs, head, includes, isNil, lensProp, map, omit, over, pluck, prop, propEq, propOr, propSatisfies, T, uniq, uniqBy, when, without } from 'ramda';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import { useEffect, useState } from 'react';
import { supplementaryCoverSelectionModeTypeEnum } from '@@viewEnums';
import useSaveSimulationPricingsGroupHealthMutation from './useSaveSimulationPricingsGroupHealthMutation.js';
import { dateTsToDate } from '@@viewHelpers/date.js';

const useSimulationPricingsGroupHealthForm = ({
  getMiddleNumber,
  getPricings
}) => {
  const { context, processHandlers } = useProcess();
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [unauthorizedModalMessage, setUnauthorizedModalMessage] = useState('');

  useEffect(() => {
    if (isNotNilOrEmpty(getPricings.error)) {
      setIsUnauthorized(true);
      setUnauthorizedModalMessage(
        'Suite indisponibilité technique, les documents et/ou les informations produits ne sont pas disponibles. Veuillez réessayer ultérieurement. Nous mettons tout en œuvre pour résoudre le problème. En nous excusant de la gêne occasionnée.'
      );
    }
  }, [getPricings]);

  const firstTariffStructureValue =
    getPricings?.tariffStructures && getPricings.tariffStructures[0].value;

  const companyCcnHasAgreement = prop('companyCcnHasAgreement', getPricings);

  const {
    pricings,
    tariffStructures,
    minimumCcnCompliantLevel,
    employerPercentShare,
    beneficiariesEnrollment,
    defaultTariffStructure,
    defaultSelectedPricings
  } = applySpec({
    employerPercentShare: propOr('0', 'employerPercentShare'),
    beneficiariesEnrollment: propOr(false, 'beneficiariesEnrollment'),
    pricings: propOr([], 'pricings'),
    tariffStructures: propOr([], 'tariffStructures'),
    minimumCcnCompliantLevel: propOr('', 'minimumCcnCompliantLevel'),
    defaultTariffStructure: propOr(
      firstTariffStructureValue,
      'tariffStructure'
    ),
    defaultSelectedPricings: compose(
      filter(prop('isPreSelected')),
      propOr([], 'pricings')
    )
  })(getPricings);

  const defaultSelectedOptions = fromPairs(
    map(
      (item) => [
        item.pricingLabel,
        compose(
          map(prop('optionCode')),
          filter(propEq('isPreSelected', true))
        )(item.options || [])
      ],
      defaultSelectedPricings
    )
  );

  const [selectedOptions, setSelectedOptions] = useState(
    defaultSelectedOptions
  );
  const [selectedPricingCodes, setSelectedPricingCodes] = useState(
    pluck('pricingLabel', defaultSelectedPricings) || []
  );

  const [loading, setLoading] = useState(false);

  const { onFormSubmit: saveSimulationPricingsGroupHealth } =
    useSaveSimulationPricingsGroupHealthMutation();

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      employerPercentShare,
      beneficiariesEnrollment,
      tariffStructure: defaultTariffStructure
    }
  });

  const maxNumberOfProducts = getMiddleNumber(
    'NIVEAUXGARANTIESETTARIFS_GENERAL_MAX_NUMBER_OF_PRODUCTS',
    1
  );

  const displayErrorPrices = any(
    (pricing) =>
      any(
        propSatisfies((val) => val === null, 'tariffStructureLabel'),
        propOr([], 'prices', pricing)
      ),
    pricings
  );

  const options = compose(
    map(({ optionCode, optionName }) => ({
      value: optionCode,
      label: optionName
    })),
    uniqBy(prop('optionCode')),
    flatten,
    filter(isNotNilOrEmpty),
    pluck('options')
  )(pricings);

  const selectedTariffStructure = form.watch('tariffStructure');

  // handle selection of options
  const toggleOption = (pricingLabel, optionCode, mode) => {
    setSelectedOptions((prev) => {
      const current = propOr([], pricingLabel, prev);

      const isSelected = includes(optionCode, current);

      const updateSelection = cond([
        [
          includes(__, [
            supplementaryCoverSelectionModeTypeEnum.MULTIPLE_OBLIGATOIRE,
            supplementaryCoverSelectionModeTypeEnum.MULTIPLE_FACULTATIF
          ]),
          () =>
            isSelected
              ? without([optionCode], current)
              : append(optionCode, current)
        ],
        [
          equals(supplementaryCoverSelectionModeTypeEnum.UNIQUE_FACULTATIF),
          () => (isSelected ? [] : [optionCode])
        ],
        [
          // when clicking on a UNIQUE_OBLIGATOIRE option, always add it
          equals(supplementaryCoverSelectionModeTypeEnum.UNIQUE_OBLIGATOIRE),
          () =>
            isSelected && equals(head(current), optionCode) ? [] : [optionCode]
        ],
        [T, () => current]
      ]);

      return assoc(pricingLabel, updateSelection(mode), prev);
    });
  };

  // handle mapping flexiproducts
  const moduleCodes = compose(
    uniq,
    pluck('moduleCode'),
    chain(prop('modules'))
  )(pricings);

  const minimumCCNIndex = findIndex(
    (pricing) => pricing.pricingLabel === minimumCcnCompliantLevel,
    pricings
  );
  const isSelected = (pricingLabel) =>
    includes(pricingLabel, selectedPricingCodes);
  const findModuleLabel = (moduleCode) =>
    compose(
      prop('moduleLabel'),
      find(propEq('moduleCode', moduleCode)),
      chain(prop('modules'))
    )(pricings);
  const findModuleGuarantees = (moduleCode) =>
    compose(
      uniqBy(prop('code')),
      map((g) => ({
        code: g.guaranteeCode,
        isTitle: g.isTitle
      })),
      chain(propOr([], 'guarantees')),
      filter(propEq('moduleCode', moduleCode)),
      chain(prop('modules'))
    )(pricings);

  const { isValid } = form.formState;

  const $onClickPrev = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);

    const selectedPricing = find(
      propEq('pricingLabel', selectedPricingCodes[0])
    )(pricings);

    if (isNil(selectedPricing)) {
      setLoading(false);
      return;
    }

    const tariffStructure = prop('tariffStructure', values);

    const filteredPricing = compose(
      // Filter and transform `totalPrices`
      over(
        lensProp('totalPrices'),
        compose(
          // Keep only `totalPrices` matching the selected tariff structure
          filter(propEq('tariffStructureCode', tariffStructure)),
          // Keep only 'withBenef' or 'withoutBenef' depending on beneficiariesEnrollment
          map(
            when(Boolean, (p) =>
              prop('beneficiariesEnrollment', values)
                ? omit(['withoutBenef'], p)
                : omit(['withBenef'], p)
            )
          )
        )
      ),
      // Filter `prices`
      over(
        lensProp('prices'),
        // Keep only prices matching the selected tariff structure
        filter(propEq('tariffStructureCode', tariffStructure))
      ),
      // Filter `options`
      over(
        lensProp('options'),
        compose(
          // Keep only options that are selected by the user
          filter((opt) =>
            includes(
              opt.optionCode,
              propOr([], selectedPricingCodes, selectedOptions)
            )
          ),
          // Filter each option's prices to keep only those matching the selected tariff structure
          map(
            over(
              lensProp('prices'),
              filter(propEq('tariffStructureCode', tariffStructure))
            )
          ),
          // Keep only options that still have at least one price after filtering by tariff structure
          filter((opt) => propOr([], 'prices', opt).length > 0)
        )
      )
    )(selectedPricing);

    const filteredPricingIndex = findIndex(
      pricing => pricing.pricingLabel === filteredPricing.pricingLabel,
      pricings
    );

    filteredPricing.isMinimaCCN = minimumCCNIndex > -1 ? filteredPricingIndex === minimumCCNIndex : false;
    filteredPricing.isConformCCN = minimumCCNIndex > -1 ? filteredPricingIndex >= minimumCCNIndex : false;

    const data = {
      ...evolve({
        employerPercentShare: Number,
        beneficiariesEnrollment: (v) => Boolean(v)
      })(values),
      pricings: filteredPricing
    };

    saveSimulationPricingsGroupHealth(data, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  const effectDate = dateTsToDate(prop('effectDate', getPricings));

  return {
    form,
    onSubmit,
    $onClickPrev,
    isValid,
    maxNumberOfProducts,
    pricings,
    options,
    tariffStructures,
    selectedTariffStructure,
    moduleCodes,
    minimumCCNIndex,
    isSelected,
    findModuleLabel,
    findModuleGuarantees,
    selectedOptions,
    toggleOption,
    displayErrorPrices,
    setSelectedPricingCodes,
    selectedPricingCodes,
    setSelectedOptions,
    effectDate,
    companyCcnHasAgreement,
    isUnauthorized,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage
  };
};

export default useSimulationPricingsGroupHealthForm;
